import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SyncIcon from '@mui/icons-material/Sync';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import { TEAM_STATUS_CHOICES } from 'Includes/teamStatus';

const TEAM_STATUS_LABELS = Object.fromEntries(
  TEAM_STATUS_CHOICES.map((opt) => [opt.value, opt.label])
);

const SUMMARY_COLUMN_KEYS = [
  'institute_name',
  'school_address',
  'fee_collection_last_used_value',
  'attendance_used_percentage',
  'diary_used_percentage',
  'last_exam_used_percentage',
  'using_efficiency',
  'coordinator_name',
];

const ALWAYS_VISIBLE_COLUMNS = new Set(['institute_name', 'school_address']);

const formatValue = (value, columnKey, isNotCreated) => {
  if (isNotCreated && !ALWAYS_VISIBLE_COLUMNS.has(columnKey)) {
    return <Typography component="span" sx={{ color: 'error.main' }}>Not Created</Typography>;
  }
  if (value === 'Never Used') {
    return <Typography component="span" sx={{ color: 'error.main' }}>{value}</Typography>;
  }
  if (columnKey === 'using_efficiency') {
    const isGreen = typeof value === 'number' && value > 50;
    return <Typography component="span" sx={{ color: isGreen ? 'success.main' : 'error.main', fontWeight: 600 }}>{value}%</Typography>;
  }
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

function ClientReportList(props) {
  const [rows, setRows] = React.useState([]);
  const [sourceColumns, setSourceColumns] = React.useState([]);
  const [tabs, setTabs] = React.useState([]);
  const [syncDates, setSyncDates] = React.useState([]);
  const [syncDateFilter, setSyncDateFilter] = React.useState('');
  const [syncDatesReady, setSyncDatesReady] = React.useState(false);
  const [schoolFilter, setSchoolFilter] = React.useState('');
  const [schoolSearchText, setSchoolSearchText] = React.useState('');
  const [schoolOptions, setSchoolOptions] = React.useState([]);
  const [schoolOptionsLoading, setSchoolOptionsLoading] = React.useState(false);
  const [tabFilter, setTabFilter] = React.useState('');
  const [tabDefaultApplied, setTabDefaultApplied] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [syncing, setSyncing] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [rowCount, setRowCount] = React.useState(0);
  const [expandedRowId, setExpandedRowId] = React.useState(null);
  const [lastSyncTime, setLastSyncTime] = React.useState(null);
  const [moduleFilter, setModuleFilter] = React.useState('');
  const [moduleValueFilter, setModuleValueFilter] = React.useState('');
  const [moduleDistinctValues, setModuleDistinctValues] = React.useState([]);
  const [coordinatorFilter, setCoordinatorFilter] = React.useState('');
  const [coordinatorOptions, setCoordinatorOptions] = React.useState([]);
  const [creationStatusFilter, setCreationStatusFilter] = React.useState('');
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState('all');
  const [sortBy, setSortBy] = React.useState('institute_name');
  const [sortDir, setSortDir] = React.useState('asc');
  const [allSchools, setAllSchools] = React.useState([]);
  const [downloadingNeverUsed, setDownloadingNeverUsed] = React.useState(false);
  const [urlParamsApplied, setUrlParamsApplied] = React.useState(false);
  const userInfo = React.useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const isSuperuser = Boolean(userInfo?.is_superuser);

  // Read URL params on mount for module pre-selection from dashboard card clicks
  React.useEffect(() => {
    if (urlParamsApplied) return;
    const params = new URLSearchParams(window.location.search);
    const urlModule = params.get('module') || '';
    const urlModuleValue = params.get('module_value') || '';
    const urlTabName = params.get('tab_name') || '';
    const urlCreationStatus = params.get('creation_status') || '';
    const urlStatusByTeam = params.get('status_by_team') || '';
    const urlSyncDate = params.get('sync_date') || '';
    if (urlModule) setModuleFilter(urlModule);
    if (urlModuleValue) setModuleValueFilter(urlModuleValue);
    if (urlTabName) {
      setTabFilter(urlTabName);
      setTabDefaultApplied(true);
    }
    if (urlCreationStatus) setCreationStatusFilter(urlCreationStatus);
    if (urlStatusByTeam) setStatusByTeamFilter(urlStatusByTeam || 'all');
    if (urlSyncDate) setSyncDateFilter(urlSyncDate);
    setUrlParamsApplied(true);
  }, [urlParamsApplied]);

  // Fetch sync dates and default to latest
  React.useEffect(() => {
    getRequest(
      GET_URL.client_track_report.api,
      { sync_dates: 1 },
      props
    ).then((response) => {
      if (response?.status === 200) {
        const dates = response.data?.data || [];
        setSyncDates(dates);
        setSyncDateFilter((current) => {
          if (current && dates.some((d) => d.date === current)) return current;
          return dates[0]?.date || '';
        });
      }
      setSyncDatesReady(true);
    }).catch(() => setSyncDatesReady(true));
  }, [props]);

  // Fetch coordinator options (from company table)
  React.useEffect(() => {
    getRequest(
      GET_URL.client_track_report.api,
      { coordinator_options: 1 },
      props
    ).then((response) => {
      if (response?.status === 200) {
        setCoordinatorOptions(response.data?.data || []);
      }
    }).catch(() => {});
  }, [props]);

  // Fetch distinct values when module filter changes
  React.useEffect(() => {
    if (!moduleFilter) {
      setModuleDistinctValues([]);
      return;
    }
    getRequest(
      GET_URL.client_track_report.api,
      {
        distinct_values: 1,
        column: moduleFilter,
        ...(tabFilter && { tab_name: tabFilter }),
      },
      props
    ).then((response) => {
      if (response?.status === 200) {
        setModuleDistinctValues(response.data?.data || []);
      }
    }).catch(() => {});
  }, [moduleFilter, tabFilter, props]);

  const fetchReports = React.useCallback(() => {
    if (!urlParamsApplied || !syncDatesReady) return;
    setLoading(true);

    getRequest(
      GET_URL.client_track_report.api,
      {
        limit: pageSize,
        pageno: page + 1,
        ...(syncDateFilter && { sync_date: syncDateFilter }),
        ...(schoolFilter.trim() && { school: schoolFilter.trim() }),
        ...(tabFilter && { tab_name: tabFilter }),
        ...(coordinatorFilter && { coordinator: coordinatorFilter }),
        ...(statusByTeamFilter && statusByTeamFilter !== 'all' && { status_by_team: statusByTeamFilter }),
        ...(creationStatusFilter && { creation_status: creationStatusFilter }),
        ...(moduleFilter && moduleValueFilter && {
          module_filter: moduleFilter,
          module_value: moduleValueFilter,
        }),
        ...(sortBy && { sort: sortBy, sort_dir: sortDir }),
      },
      props
    )
      .then((response) => {
        setLoading(false);

        if (response?.status === 200) {
          console.log(response.data,"data--------------");
          if (!tabDefaultApplied) {
            const defaultTab = response.data?.default_tab || '';
            setTabDefaultApplied(true);
            if (defaultTab && !tabFilter) {
              setTabFilter(defaultTab);
              setLoading(false);
              return;
            }
          }

          const apiRows = response.data?.data || [];
          const serverCount = response.data?.count;

          setRows(apiRows);
          setSourceColumns(response.data?.columns || []);
          setTabs(response.data?.tabs || []);
          if (Array.isArray(response.data?.sync_dates) && response.data.sync_dates.length) {
            setSyncDates(response.data.sync_dates);
          }

          // Use server-side count for pagination when available
          if (serverCount !== undefined && serverCount !== null) {
            setRowCount(serverCount);
          } else {
            setRowCount(apiRows.length);
          }

          setLastSyncTime(response.data?.last_sync_time || null);
          setExpandedRowId(null);
        }
      })
      .catch(() => setLoading(false));
  }, [
    page,
    pageSize,
    props,
    schoolFilter,
    tabFilter,
    coordinatorFilter,
    statusByTeamFilter,
    creationStatusFilter,
    moduleFilter,
    moduleValueFilter,
    syncDateFilter,
    urlParamsApplied,
    syncDatesReady,
    sortBy,
    sortDir,
    tabDefaultApplied,
  ]);

  React.useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const fetchSchoolOptions = React.useCallback((searchText = '') => {
    setSchoolOptionsLoading(true);
    getRequest(
      GET_URL.client_track_report.api,
      {
        institute_options: 1,
        ...(searchText.trim() && { search: searchText.trim() }),
        ...(tabFilter && { tab_name: tabFilter }),
      },
      props
    ).then((response) => {
      setSchoolOptionsLoading(false);
      if (response?.status === 200) {
        setSchoolOptions(response.data?.data || []);
      }
    }).catch(() => setSchoolOptionsLoading(false));
  }, [props, tabFilter]);

  React.useEffect(() => {
    fetchSchoolOptions(schoolSearchText);
  }, [fetchSchoolOptions, schoolSearchText]);

  React.useEffect(() => {
    getRequest(
      GET_URL.client_track_report.api,
      {
        institute_directory: 1,
      },
      props
    ).then((response) => {
      if (response?.status === 200) {
        setAllSchools(response.data?.data || []);
      }
    });
  }, [props]);

  const handleDownloadNeverUsed = () => {
    if (!moduleFilter) return;

    setDownloadingNeverUsed(true);
    getRequest(
      GET_URL.client_track_report.api,
      {
        never_used_export: 1,
        module: moduleFilter,
        ...(schoolFilter.trim() && { school: schoolFilter.trim() }),
        ...(tabFilter && { tab_name: tabFilter }),
      },
      { ...props, responseType: 'blob' }
    )
      .then((response) => {
        if (response?.status === 200) {
          const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
          const fileURL = window.URL.createObjectURL(blob);
          const contentDisposition =
            response?.headers?.['content-disposition'] || response?.headers?.['Content-Disposition'];
          let filename = `client_report_never_used_${moduleFilter}.csv`;
          if (typeof contentDisposition === 'string') {
            const match = contentDisposition.match(/filename="?([^"]+)"?/i);
            if (match?.[1]) filename = match[1];
          }
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          window.URL.revokeObjectURL(fileURL);
          document.body.removeChild(link);
        }
      })
      .finally(() => setDownloadingNeverUsed(false));
  };

  const handleSync = () => {
    const transactionId = Date.now();
    setSyncing(true);
    setMessage('');
    postRequest(POST_URL.client_track_report.api, { transaction_id: transactionId }, props).then((response) => {
      setSyncing(false);
      if (response?.status === 200) {
        setLastSyncTime(response.data?.last_sync_time || lastSyncTime);
        setMessage('Report sync started. Data will update after the callback is received.');
      }
    }).catch(() => {
      setSyncing(false);
    });
  };

  const uniqueTabs = React.useMemo(() => {
    const seen = new Set();
    return (tabs || []).filter((tab) => {
      const key = (tab || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [tabs]);

  const summaryColumns = React.useMemo(() => {
    const instituteColumn = (sourceColumns || []).find(
      (item) => item.schemacolumn === 'institute_name'
    );

    // When module selected
    if (moduleFilter) {
      const selectedModuleColumn = (sourceColumns || []).find(
        (item) => item.schemacolumn === moduleFilter
      );
      const addressColumn = (sourceColumns || []).find(
        (item) => item.schemacolumn === 'school_address'
      );

      const cols = [];

      if (instituteColumn) {
        cols.push(instituteColumn);
      }

      if (addressColumn) {
        cols.push(addressColumn);
      }

      if (
        selectedModuleColumn &&
        selectedModuleColumn.schemacolumn !== 'institute_name' &&
        selectedModuleColumn.schemacolumn !== 'school_address'
      ) {
        cols.push(selectedModuleColumn);
      }

      return cols;
    }

    const selectedColumns = (sourceColumns || []).filter((item) =>
      SUMMARY_COLUMN_KEYS.includes(item.schemacolumn)
    );

    return selectedColumns.length
      ? selectedColumns
      : (sourceColumns || []).slice(0, 6);
  }, [sourceColumns, moduleFilter]);

  const detailColumns = React.useMemo(() => {
    const summaryKeys = new Set(summaryColumns.map((item) => item.schemacolumn));
    return (sourceColumns || []).filter((item) => !summaryKeys.has(item.schemacolumn));
  }, [sourceColumns, summaryColumns]);

  const totalColumnCount = summaryColumns.length + 2;

  // Find module display name for the active filter chip
  const activeModuleLabel = React.useMemo(() => {
    if (!moduleFilter) return '';
    const col = (sourceColumns || []).find((c) => c.schemacolumn === moduleFilter);
    return col ? col.column : moduleFilter;
  }, [moduleFilter, sourceColumns]);

  // Find coordinator display name for the active filter chip
  const activeCoordinatorLabel = React.useMemo(() => {
    if (!coordinatorFilter) return '';
    const opt = coordinatorOptions.find((c) => c.id === coordinatorFilter);
    return opt ? opt.name : coordinatorFilter;
  }, [coordinatorFilter, coordinatorOptions]);

  const activeSyncDateLabel = React.useMemo(() => {
    if (!syncDateFilter) return '';
    const entry = syncDates.find((d) => d.date === syncDateFilter);
    return entry?.label || syncDateFilter;
  }, [syncDateFilter, syncDates]);

  const handleSort = (field) => {
    setPage(0);
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortDir('asc');
  };

  const renderSortableHeader = (field, label) => (
    <TableCell
      key={field}
      sortDirection={sortBy === field ? sortDir : false}
      sx={{ fontWeight: 700, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      <TableSortLabel
        active={sortBy === field}
        direction={sortBy === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
        hideSortIcon={false}
        sx={{
          fontWeight: 700,
          '& .MuiTableSortLabel-icon': { fontSize: 16, opacity: 0.7 },
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box p={2}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" fontWeight={700}>Client Report</Typography>
          <Typography variant="body2" color="text.secondary">
            Sync and view school usage report data.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last Sync: {lastSyncTime ? new Date(lastSyncTime).toLocaleString() : 'Not synced yet'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4} textAlign="right">
          {moduleFilter && (
            <Button
              variant="outlined"
              startIcon={<DownloadOutlinedIcon />}
              onClick={handleDownloadNeverUsed}
              disabled={downloadingNeverUsed}
              sx={{ mr: 1 }}
            >
              {downloadingNeverUsed ? 'Downloading...' : 'Download Never Used'}
            </Button>
          )}
          {isSuperuser && (
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Sync Report'}
            </Button>
          )}
        </Grid>
      </Grid>

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Sync Date"
              value={syncDateFilter}
              onChange={(event) => {
                setSyncDateFilter(event.target.value);
                setPage(0);
              }}
              disabled={!syncDates.length}
            >
              {syncDates.length === 0 && (
                <MenuItem value="">No syncs yet</MenuItem>
              )}
              {syncDates.map((d) => (
                <MenuItem key={d.date} value={d.date}>
                  {d.label}
                  {d.saved_count != null ? ` (${d.saved_count} records)` : ''}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              freeSolo
              options={schoolOptions}
              value={schoolFilter || null}
              inputValue={schoolSearchText}
              loading={schoolOptionsLoading}
              onChange={(event, value) => {
                const selectedValue = value || '';
                setSchoolFilter(selectedValue);
                setSchoolSearchText(selectedValue);
                setPage(0);
              }}
              onInputChange={(event, value, reason) => {
                setSchoolSearchText(value || '');
                if (reason === 'clear') {
                  setSchoolFilter('');
                  setPage(0);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter School"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setSchoolFilter(schoolSearchText);
                      setPage(0);
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {schoolOptionsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Academic Year"
              value={tabFilter}
              onChange={(event) => {
                setTabFilter(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {uniqueTabs.map((tab) => (
                <MenuItem key={tab} value={tab}>{tab}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Coordinator"
              value={coordinatorFilter}
              onChange={(event) => {
                setCoordinatorFilter(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {coordinatorOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.name || opt.id}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Report Status"
              value={creationStatusFilter}
              onChange={(event) => {
                setCreationStatusFilter(event.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="created">Created</MenuItem>
              <MenuItem value="not_created">Not Created</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Team Status"
              value={statusByTeamFilter}
              onChange={(event) => {
                setStatusByTeamFilter(event.target.value);
                setPage(0);
              }}
            >
              {TEAM_STATUS_CHOICES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Module"
              value={moduleFilter}
              onChange={(event) => {
                setModuleFilter(event.target.value);
                setModuleValueFilter('');
              }}
            >
              <MenuItem value="">All</MenuItem>
              {sourceColumns.map((column) => (
                <MenuItem
                  key={column.schemacolumn}
                  value={column.schemacolumn}
                >
                  {column.column}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* Module inner filter - show distinct values when module is selected */}
        {moduleFilter && moduleDistinctValues.length > 0 && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                size="small"
                label={`Filter by ${activeModuleLabel} value`}
                value={moduleValueFilter}
                onChange={(event) => {
                  setModuleValueFilter(event.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All values</MenuItem>
                {moduleDistinctValues.map((val) => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        )}

        {/* Active filter chips */}
        {(moduleFilter || coordinatorFilter || tabFilter || schoolFilter || creationStatusFilter || (statusByTeamFilter && statusByTeamFilter !== 'all') || syncDateFilter) && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
            <FilterListIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.3 }} />
            {syncDateFilter && (
              <Chip
                label={`Sync: ${activeSyncDateLabel}`}
                size="small"
                color="primary"
                variant="filled"
              />
            )}
            {tabFilter && (
              <Chip
                label={`Year: ${tabFilter}`}
                size="small"
                color="primary"
                variant="outlined"
                onDelete={() => { setTabFilter(''); setPage(0); }}
              />
            )}
            {coordinatorFilter && (
              <Chip
                label={`Coordinator: ${activeCoordinatorLabel}`}
                size="small"
                color="secondary"
                variant="outlined"
                onDelete={() => { setCoordinatorFilter(''); setPage(0); }}
              />
            )}
            {moduleFilter && (
              <Chip
                label={`Module: ${activeModuleLabel}`}
                size="small"
                color="info"
                variant="outlined"
                onDelete={() => { setModuleFilter(''); setModuleValueFilter(''); }}
              />
            )}
            {moduleValueFilter && (
              <Chip
                label={`Value: ${moduleValueFilter}`}
                size="small"
                color="default"
                variant="outlined"
                onDelete={() => { setModuleValueFilter(''); setPage(0); }}
              />
            )}
            {creationStatusFilter && (
              <Chip
                label={`Report: ${creationStatusFilter === 'not_created' ? 'Not Created' : 'Created'}`}
                size="small"
                color="success"
                variant="outlined"
                onDelete={() => { setCreationStatusFilter(''); setPage(0); }}
              />
            )}
            {statusByTeamFilter && statusByTeamFilter !== 'all' && (
              <Chip
                label={`Team: ${TEAM_STATUS_LABELS[statusByTeamFilter] || statusByTeamFilter}`}
                size="small"
                color="default"
                variant="outlined"
                onDelete={() => { setStatusByTeamFilter('all'); setPage(0); }}
              />
            )}
            {schoolFilter && (
              <Chip
                label={`School: ${schoolFilter}`}
                size="small"
                color="warning"
                variant="outlined"
                onDelete={() => { setSchoolFilter(''); setSchoolSearchText(''); setPage(0); }}
              />
            )}
          </Stack>
        )}
      </Paper>

      <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell width={48} />
                {renderSortableHeader('tab_name', 'Academic Year')}
                {summaryColumns.map((column) => (
                  renderSortableHeader(column.schemacolumn, column.column)
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={totalColumnCount}>
                    <Typography align="center" sx={{ py: 4 }}>Loading...</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length ? rows.map((row, index) => {
                // Sync-date / flattened rows may lack unique ids (or share the same id);
                // use tab + institute + page index so only the clicked row expands.
                const rowKey = `${row.tab_name || ''}::${row.institute_name || ''}::${page}::${index}`;
                const isExpanded = expandedRowId === rowKey;
                return (
                  <React.Fragment key={rowKey}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedRowId(isExpanded ? null : rowKey)}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{formatValue(row.tab_name)}</TableCell>
                      {summaryColumns.map((column) => (
                        <TableCell key={column.schemacolumn}>
                          {formatValue(row[column.schemacolumn], column.schemacolumn, row.is_not_created)}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ py: 0 }} colSpan={totalColumnCount}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: '#fbfdff' }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                              More Report Data
                            </Typography>
                            <Grid container spacing={2}>
                              {detailColumns.map((column) => (
                                <Grid item xs={12} md={4} key={column.schemacolumn}>
                                  <Box sx={{ borderBottom: '1px solid #eef2f7', pb: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {column.column}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                      {formatValue(row[column.schemacolumn], column.schemacolumn, row.is_not_created)}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={totalColumnCount}>
                    <Typography align="center" sx={{ py: 4 }}>No report data found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setPageSize(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
}

export default ClientReportList;
